import { useState, useEffect } from 'react';
import { collection, addDoc, serverTimestamp, query, where, getDocs, limit, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { Card, Icon, Loading, Modal, PageHead, useToast } from '../ui';

const BUILT_IN_TEMPLATES = [
  { id: 'bi1', title: 'Size özel teklif', body: 'Bugün sipariş verin, özel indirimden yararlanın. Sizi bekliyoruz!', type: 'coupon' },
  { id: 'bi2', title: 'Günün fırsatı', body: 'Sınırlı süre için özel fiyatlar sizi bekliyor. Hemen sipariş verin!', type: 'general' },
  { id: 'bi3', title: 'Menümüze yeni lezzetler geldi', body: 'Yeni ürünlerimizi keşfedin, afiyet olsun!', type: 'general' },
  { id: 'bi4', title: 'Sizi özledik', body: 'Bir süredir görüşemedik. Künefeniz hazır, buyurun!', type: 'general' },
  { id: 'bi5', title: 'Teşekkürler', body: 'Kadıhan\'ı tercih ettiğiniz için teşekkür ederiz. Yakında görüşmek üzere!', type: 'general' },
];

const TYPES = [
  { value: 'general', label: 'Ana sayfa', hint: 'Uygulamanın ana sayfasını açar' },
  { value: 'order_update', label: 'Siparişlerim', hint: 'Sipariş listesini açar' },
  { value: 'coupon', label: 'Kuponlarım', hint: 'Kupon sayfasını açar' },
];

function UserPickerModal({ onSelect, onClose }) {
  const [users, setUsers] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    getDocs(query(collection(db, 'users'), orderBy('name'), limit(500)))
      .then((snap) => setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch(() => setUsers([]));
  }, []);

  const q = search.trim().toLocaleLowerCase('tr-TR');
  const filtered = (users || []).filter((u) => !q || [u.name, u.phone, u.email].some((f) => String(f || '').toLocaleLowerCase('tr-TR').includes(q)));

  return (
    <Modal title="Müşteri seç" width={480} onClose={onClose}>
      <div className="k-search" style={{ marginBottom: 12 }}>
        <Icon name="search" size={16} />
        <input className="k-input" autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ad, telefon veya e-posta" />
      </div>
      {users === null ? <Loading /> : filtered.length === 0 ? <p className="kn-muted">Müşteri bulunamadı.</p> : (
        <div className="kn-users">
          {filtered.map((u) => (
            <button key={u.id} type="button" className="kn-user" onClick={() => onSelect(u)}>
              <span className="kn-avatar">{(u.name || '?').charAt(0).toLocaleUpperCase('tr-TR')}</span>
              <span><strong>{u.name || 'İsimsiz'}</strong><em>{u.phone || u.email}</em></span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

function Notifications() {
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [type, setType] = useState('general');
  const [target, setTarget] = useState('all');
  const [phoneInput, setPhoneInput] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);

  const loadTemplates = async () => {
    try {
      const snap = await getDocs(query(collection(db, 'notificationTemplates'), orderBy('createdAt', 'desc'), limit(20)));
      setSavedTemplates(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
  };
  useEffect(() => { loadTemplates(); }, []);

  const applyTemplate = (tpl) => { setTitle(tpl.title); setBody(tpl.body); if (tpl.type) setType(tpl.type); };

  const saveTemplate = async () => {
    if (!title.trim() || !body.trim()) { toast('Taslak için başlık ve mesaj gerekli.', 'error'); return; }
    setSavingTemplate(true);
    try {
      await addDoc(collection(db, 'notificationTemplates'), { title: title.trim(), body: body.trim(), type, createdAt: serverTimestamp() });
      await loadTemplates();
      toast('Taslak kaydedildi.', 'ok');
    } catch (e) { toast(`Kaydedilemedi: ${e.message}`, 'error'); }
    setSavingTemplate(false);
  };

  const deleteTemplate = async (id) => {
    await deleteDoc(doc(db, 'notificationTemplates', id));
    setSavedTemplates((prev) => prev.filter((t) => t.id !== id));
  };

  // Gönderim öncesi doğrulama; geçerse onay penceresi açılır.
  const prepareSend = async () => {
    setError('');
    if (!title.trim() || !body.trim()) { setError('Başlık ve mesaj boş bırakılamaz.'); return; }
    if (target === 'user' && !selectedUser) {
      if (!phoneInput.trim()) { setError('Müşteri seçin veya telefon numarası girin.'); return; }
      const phone = phoneInput.trim().startsWith('+90') ? phoneInput.trim().replace(/\s/g, '') : `+90${phoneInput.trim().replace(/\s/g, '').replace(/^0/, '')}`;
      if (!/^\+905[0-9]{9}$/.test(phone)) { setError('Geçersiz telefon numarası. Örnek: 05XX XXX XX XX'); return; }
      const snap = await getDocs(query(collection(db, 'users'), where('phone', '==', phone), limit(1)));
      if (snap.empty) { setError('Bu telefon numarasına kayıtlı müşteri bulunamadı.'); return; }
      setSelectedUser({ id: snap.docs[0].id, ...snap.docs[0].data() });
    }
    setConfirmSend(true);
  };

  const send = async () => {
    setSending(true);
    try {
      await addDoc(collection(db, 'broadcastNotifications'), {
        title: title.trim(),
        body: body.trim(),
        type,
        targetUserId: target === 'user' ? selectedUser?.id || null : null,
        createdAt: serverTimestamp(),
      });
      toast(target === 'all' ? 'Bildirim tüm müşterilere gönderildi.' : 'Bildirim gönderildi.', 'ok');
      setTitle(''); setBody(''); setPhoneInput(''); setSelectedUser(null);
    } catch {
      toast('Gönderilemedi, tekrar deneyin.', 'error');
    }
    setSending(false);
    setConfirmSend(false);
  };

  return (
    <div className="k-page" style={{ maxWidth: 1120 }}>
      {showPicker && <UserPickerModal onSelect={(u) => { setSelectedUser(u); setShowPicker(false); setPhoneInput(''); }} onClose={() => setShowPicker(false)} />}

      <PageHead desc="Uygulamayı kullanan müşterilere anlık bildirim gönderin." />

      <div className="kn-layout">
        <Card title="Yeni bildirim">
          <label className="k-field">
            <span>Başlık</span>
            <input className="k-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={60} placeholder="Örn. Künefe saati!" />
            <small>{title.length}/60</small>
          </label>
          <label className="k-field">
            <span>Mesaj</span>
            <textarea className="k-textarea" value={body} onChange={(e) => setBody(e.target.value)} maxLength={200} rows={3} placeholder="Bildirim metni" />
            <small>{body.length}/200</small>
          </label>

          <div className="k-field">
            <span>Dokununca açılacak sayfa</span>
            <div className="kn-types">
              {TYPES.map((t) => (
                <button key={t.value} type="button" className={`kn-type${type === t.value ? ' on' : ''}`} onClick={() => setType(t.value)}>
                  <strong>{t.label}</strong><em>{t.hint}</em>
                </button>
              ))}
            </div>
          </div>

          <div className="k-field">
            <span>Kime?</span>
            <div className="k-seg">
              <button type="button" className={target === 'all' ? 'on' : ''} onClick={() => { setTarget('all'); setSelectedUser(null); setPhoneInput(''); }}>Tüm müşteriler</button>
              <button type="button" className={target === 'user' ? 'on' : ''} onClick={() => setTarget('user')}>Belirli müşteri</button>
            </div>
          </div>

          {target === 'user' && (
            <div className="kn-target">
              {selectedUser ? (
                <div className="kn-selected">
                  <span className="kn-avatar">{(selectedUser.name || '?').charAt(0).toLocaleUpperCase('tr-TR')}</span>
                  <span style={{ flex: 1 }}><strong>{selectedUser.name || 'İsimsiz'}</strong><em>{selectedUser.phone || selectedUser.email}</em></span>
                  <button type="button" className="k-icon-btn" onClick={() => setSelectedUser(null)} aria-label="Kaldır"><Icon name="x" size={16} /></button>
                </div>
              ) : (
                <>
                  <button type="button" className="k-btn k-btn-secondary k-btn-block" onClick={() => setShowPicker(true)}><Icon name="users" size={15} />Listeden seç</button>
                  <div className="kn-or"><span>veya telefon numarası</span></div>
                  <input className="k-input" value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)} placeholder="05XX XXX XX XX" />
                </>
              )}
            </div>
          )}

          {error && <div className="k-alert error" style={{ marginTop: 14 }}>{error}</div>}

          <div className="kn-actions">
            <button type="button" className="k-btn k-btn-secondary" onClick={saveTemplate} disabled={savingTemplate}><Icon name="note" size={15} />{savingTemplate ? 'Kaydediliyor…' : 'Taslak olarak kaydet'}</button>
            <button type="button" className="k-btn k-btn-primary" onClick={prepareSend} disabled={sending}><Icon name="bell" size={15} />Gönder</button>
          </div>
        </Card>

        <div className="kn-side">
          <div className="kn-phone" aria-label="Önizleme">
            <div className="kn-phone-label">Müşterinin göreceği bildirim</div>
            <div className="kn-notif">
              <img src="/kadihan-logo.jpg" alt="" />
              <div>
                <div className="kn-notif-top"><strong>Kadıhan</strong><span>şimdi</span></div>
                <div className="kn-notif-title">{title || 'Bildirim başlığı'}</div>
                <div className="kn-notif-body">{body || 'Mesajınız burada görünecek.'}</div>
              </div>
            </div>
          </div>

          <Card title="Taslaklar" sub="Tıklayınca forma yüklenir.">
            <div className="kn-tpls">
              {[...savedTemplates.map((t) => ({ ...t, saved: true })), ...BUILT_IN_TEMPLATES].map((tpl) => (
                <div key={tpl.id} className="kn-tpl">
                  <button type="button" onClick={() => applyTemplate(tpl)}>
                    <strong>{tpl.title}</strong><em>{tpl.body}</em>
                  </button>
                  {tpl.saved && <button type="button" className="k-icon-btn kn-del" title="Taslağı sil" onClick={() => deleteTemplate(tpl.id)}><Icon name="trash" size={15} /></button>}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {confirmSend && (
        <Modal
          title="Bildirimi gönder"
          width={440}
          onClose={() => setConfirmSend(false)}
          footer={(
            <>
              <button type="button" className="k-btn k-btn-secondary" onClick={() => setConfirmSend(false)}>Vazgeç</button>
              <button type="button" className="k-btn k-btn-primary" disabled={sending} onClick={send}>{sending ? 'Gönderiliyor…' : 'Gönder'}</button>
            </>
          )}
        >
          <strong>“{title}”</strong> bildirimi {target === 'all' ? <strong>tüm müşterilere</strong> : <><strong>{selectedUser?.name || 'seçilen müşteriye'}</strong></>} gönderilecek. Gönderilen bildirim geri alınamaz.
        </Modal>
      )}

      <style>{`
        .kn-layout { display: grid; grid-template-columns: minmax(0, 1fr) 360px; gap: 16px; align-items: start; }
        .kn-muted { color: var(--k-muted); }
        .kn-types { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
        .kn-type { text-align: left; padding: 10px 12px; border: 1px solid var(--k-line-strong); border-radius: 6px; background: #fff; display: grid; gap: 2px; }
        .kn-type strong { font-size: 13.5px; color: var(--k-ink); }
        .kn-type em { font-style: normal; font-size: 12px; color: var(--k-muted); }
        .kn-type.on { border-color: var(--k-green); background: var(--k-green-soft); box-shadow: inset 0 0 0 1px var(--k-green); }
        .kn-target { display: grid; gap: 10px; }
        .kn-or { display: flex; align-items: center; gap: 8px; color: var(--k-faint); font-size: 12px; }
        .kn-or::before, .kn-or::after { content: ""; flex: 1; height: 1px; background: var(--k-line); }
        .kn-selected { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border: 1px solid var(--k-green); border-radius: 6px; background: var(--k-green-soft); }
        .kn-selected strong, .kn-user strong { display: block; color: var(--k-ink); }
        .kn-selected em, .kn-user em { display: block; font-style: normal; font-size: 12px; color: var(--k-muted); }
        .kn-avatar { width: 34px; height: 34px; border-radius: 50%; background: var(--k-green); color: #fff; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; flex: none; }
        .kn-users { display: grid; max-height: 50vh; overflow: auto; }
        .kn-user { display: flex; gap: 10px; align-items: center; padding: 10px 8px; border: 0; background: none; text-align: left; border-radius: 8px; }
        .kn-user:hover { background: #F4F5F2; }
        .kn-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 18px; padding-top: 16px; border-top: 1px solid var(--k-line); }
        .kn-side { display: grid; gap: 16px; position: sticky; top: 16px; }
        .kn-phone { border: 1px solid var(--k-line); border-radius: 8px; padding: 12px; background: #fff; }
        .kn-phone-label { font-size: 12.5px; font-weight: 600; margin-bottom: 8px; }
        .kn-notif { display: flex; gap: 10px; padding: 10px; border-radius: 6px; background: var(--k-subtle); border: 1px solid var(--k-line); }
        .kn-notif img { width: 34px; height: 34px; border-radius: 8px; flex: none; }
        .kn-notif > div { min-width: 0; flex: 1; }
        .kn-notif-top { display: flex; justify-content: space-between; font-size: 11.5px; color: var(--k-muted); }
        .kn-notif-top strong { color: var(--k-ink); font-weight: 600; letter-spacing: .3px; text-transform: uppercase; font-size: 11px; }
        .kn-notif-title { font-weight: 700; color: var(--k-ink); font-size: 14px; margin-top: 2px; overflow-wrap: anywhere; }
        .kn-notif-body { font-size: 13px; color: var(--k-text); overflow-wrap: anywhere; }
        .kn-tpls { display: grid; gap: 8px; max-height: 360px; overflow: auto; }
        .kn-tpl { display: flex; align-items: flex-start; gap: 4px; border: 1px solid var(--k-line); border-radius: 6px; }
        .kn-tpl > button:first-child { flex: 1; text-align: left; border: 0; background: none; padding: 10px 12px; display: grid; gap: 2px; border-radius: 6px; }
        .kn-tpl > button:first-child:hover { background: #F7F8F6; }
        .kn-tpl strong { font-size: 13px; color: var(--k-ink); }
        .kn-tpl em { font-style: normal; font-size: 12px; color: var(--k-muted); }
        .kn-del { color: var(--k-red); margin: 6px 6px 0 0; }
        @media (max-width: 1100px) { .kn-layout { grid-template-columns: 1fr; } .kn-side { position: static; } .kn-types { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}

export default Notifications;
